import { Dumbbell, Home, TestTube, User, Utensils } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

interface RiskStatsInterstitialProps {
  onNext: () => void;
  age?: string;
  sex?: string;
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function PeopleRow({ filled, total }: { filled: number; total: number }) {
  return (
    <div className="mt-6 flex justify-center gap-4">
      {Array.from({ length: total }, (_, i) => {
        const isFilled = i < filled;
        const stroke = isFilled ? '#7CF1C7' : 'rgba(255,255,255,0.35)';
        const fill = isFilled ? 'rgba(124,241,199,0.12)' : 'transparent';
        const delay = `${i * 150}ms`;
        return (
          <div key={i} className="transition-all duration-500" style={{ transitionDelay: delay }}>
            <svg
              width="36"
              height="52"
              viewBox="0 0 36 52"
              fill="none"
              className="transition-all duration-500"
              style={{ transitionDelay: delay }}
            >
              <circle
                cx="18"
                cy="9"
                r="6.5"
                stroke={stroke}
                strokeWidth="1.8"
                fill="none"
                className="transition-colors duration-500"
                style={{ transitionDelay: delay }}
              />
              <path
                d="M6 24 C6 18 10 16 18 16 C26 16 30 18 30 24 L28 46 C28 48 24 48 24 46 L24 42 L12 42 L12 46 C12 48 8 48 8 46 Z"
                stroke={stroke}
                strokeWidth="1.8"
                fill={fill}
                strokeLinejoin="round"
                className="transition-colors duration-500"
                style={{ transitionDelay: delay }}
              />
            </svg>
          </div>
        );
      })}
    </div>
  );
}

const FACTOR_ICONS = [
  { Icon: Utensils, label: 'Diet' },
  { Icon: Dumbbell, label: 'Exercise' },
  { Icon: TestTube, label: 'Bloodwork' },
  { Icon: User, label: 'Genetics' },
  { Icon: Home, label: 'Environment' },
];

// ─── Personalized stat lookup ─────────────────────────────────────────────────

interface AgeStat {
  pct: number;
  filled: number;
  descriptor: string;
}

const DEFAULT_STAT: AgeStat = { pct: 50, filled: 5, descriptor: 'about 5 will get it' };

const STATS: Record<string, AgeStat> = {
  'Female|under-20': { pct: 45, filled: 5, descriptor: 'about 5 will get it' },
  'Female|20s': { pct: 45, filled: 5, descriptor: 'about 5 will get it' },
  'Female|30s': { pct: 45, filled: 5, descriptor: 'about 5 will get it' },
  'Female|40s': { pct: 44, filled: 5, descriptor: 'about 5 will get it' },
  'Female|50s': { pct: 40, filled: 4, descriptor: '4 will get it' },
  'Female|60s': { pct: 34, filled: 3, descriptor: 'about 3 to 4 will get it' },
  'Female|70+': { pct: 28, filled: 3, descriptor: 'about 3 will get it' },
  'Male|under-20': { pct: 54, filled: 5, descriptor: 'about 5 will get it' },
  'Male|20s': { pct: 54, filled: 5, descriptor: 'about 5 will get it' },
  'Male|30s': { pct: 54, filled: 5, descriptor: 'about 5 will get it' },
  'Male|40s': { pct: 53, filled: 5, descriptor: 'about 5 will get it' },
  'Male|50s': { pct: 51, filled: 5, descriptor: 'about 5 will get it' },
  'Male|60s': { pct: 45, filled: 5, descriptor: 'about 5 will get it' },
  'Male|70+': { pct: 38, filled: 4, descriptor: 'about 4 will get it' },
};

const AGE_LABELS: Record<string, string> = {
  'under-20': 'in their teens',
  '20s': 'in their 20s',
  '30s': 'in their 30s',
  '40s': 'in their 40s',
  '50s': 'in their 50s',
  '60s': 'in their 60s',
  '70+': 'over 70',
};

// ─── Main component ──────────────────────────────────────────────────────────

export function RiskStatsInterstitial({ onNext, age, sex }: RiskStatsInterstitialProps) {
  const [phase, setPhase] = useState(0);
  const [peopleFilled, setPeopleFilled] = useState(0);
  const [iconsVisible, setIconsVisible] = useState(0);
  const [iconsGathered, setIconsGathered] = useState(false);
  const [meterAnimating, setMeterAnimating] = useState(false);
  const [fadeState, setFadeState] = useState<'in' | 'out' | 'visible'>('visible');

  const isDefault = !age || !sex;
  const stat = isDefault ? DEFAULT_STAT : (STATS[`${sex}|${age}`] ?? DEFAULT_STAT);
  const sexLabel = sex === 'Female' ? 'women' : sex === 'Male' ? 'men' : 'people';
  const ageLabel = age ? (AGE_LABELS[age] ?? '') : '';
  const headline = isDefault
    ? `In general, people have about a ${stat.pct}% chance of getting cancer in their lifetime.`
    : `In general, ${sexLabel} ${ageLabel} have a ${stat.pct}% chance of getting cancer in their lifetime.`;
  const subText = `That means out of 10 ${sexLabel}, ${stat.descriptor}.`;

  const advanceToPhase = useCallback(
    (next: number) => {
      setFadeState('out');
      setTimeout(() => {
        setPhase(next);
        setFadeState('in');
        setTimeout(() => setFadeState('visible'), 50);
      }, 400);
    },
    [],
  );

  // Phase 0: people icons fill in, then auto-advance
  useEffect(() => {
    if (phase !== 0) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    for (let i = 1; i <= stat.filled; i++) {
      timers.push(setTimeout(() => setPeopleFilled(i), 600 + (i - 1) * 400));
    }
    timers.push(setTimeout(() => advanceToPhase(1), 600 + stat.filled * 400 + 1200));
    return () => timers.forEach(clearTimeout);
  }, [phase, stat.filled, advanceToPhase]);

  // Phase 1: factor icons appear, gather, then auto-advance
  useEffect(() => {
    if (phase !== 1) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    FACTOR_ICONS.forEach((_, i) => {
      timers.push(setTimeout(() => setIconsVisible(i + 1), 400 + i * 300));
    });
    timers.push(setTimeout(() => setIconsGathered(true), 400 + FACTOR_ICONS.length * 300 + 500));
    timers.push(setTimeout(() => advanceToPhase(2), 400 + FACTOR_ICONS.length * 300 + 2000));
    return () => timers.forEach(clearTimeout);
  }, [phase, advanceToPhase]);

  // Phase 2: risk meter ticker animation
  const [tickerPosition, setTickerPosition] = useState(15);
  const [tickerDirection, setTickerDirection] = useState(1);

  useEffect(() => {
    if (phase !== 2) return;
    setMeterAnimating(true);
    const interval = setInterval(() => {
      setTickerPosition((prev) => {
        const next = prev + tickerDirection * 0.8;
        if (next >= 80) {
          setTickerDirection(-1);
          return 80;
        }
        if (next <= 15) {
          setTickerDirection(1);
          return 15;
        }
        return next;
      });
    }, 30);
    return () => clearInterval(interval);
  }, [phase, tickerDirection]);

  const fadeClass =
    fadeState === 'in'
      ? 'opacity-0 translate-y-4'
      : fadeState === 'out'
        ? 'opacity-0 -translate-y-4'
        : 'opacity-100 translate-y-0';

  return (
    <div className="mx-auto w-full max-w-md">
      <div className={`transition-all duration-400 ${fadeClass}`}>
        {phase === 0 && (
          <div className="space-y-6 px-4 text-center">
            <p className="text-xl font-semibold leading-snug text-white md:text-2xl">{headline}</p>
            <p className="text-lg text-white/65">{subText}</p>
            <PeopleRow filled={peopleFilled} total={10} />
          </div>
        )}

        {phase === 1 && (
          <div className="space-y-6 px-4 text-center">
            <p className="text-xl font-semibold leading-snug text-white md:text-2xl">
              But there are <span className="text-[#7CF1C7]">hundreds of personal factors</span> that influence your risk.
            </p>
            <div
              className={`mt-8 flex justify-center transition-all duration-700 ${iconsGathered ? 'gap-1' : 'gap-4'}`}
            >
              {FACTOR_ICONS.map(({ Icon, label }, i) => (
                <div
                  key={label}
                  className={`flex flex-col items-center transition-all duration-500 ${
                    i < iconsVisible ? 'scale-100 opacity-100' : 'translate-y-6 scale-75 opacity-0'
                  } ${iconsGathered ? 'scale-90' : ''}`}
                  style={{ transitionDelay: `${i * 50}ms` }}
                >
                  <div
                    className={`flex items-center justify-center rounded-2xl bg-[rgba(124,241,199,0.15)] transition-all duration-500 ${
                      iconsGathered ? 'h-12 w-12 rounded-xl' : 'h-14 w-14'
                    }`}
                  >
                    <Icon className="h-6 w-6 text-[#7CF1C7]" />
                  </div>
                  <span
                    className={`mt-1.5 text-xs text-white/55 transition-all duration-300 ${
                      iconsGathered ? 'h-0 opacity-0' : 'opacity-100'
                    }`}
                  >
                    {label}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {phase === 2 && (
          <div className="space-y-8 px-4 text-center">
            <p className="text-xl font-semibold leading-snug text-white md:text-2xl">
              Catch analyzes them all to tell you <span className="text-[#7CF1C7]">exactly where you stand</span>.
            </p>
            <div className="mt-8 space-y-3">
              <div className="relative h-4 w-full overflow-hidden rounded-full bg-white/12">
                <div
                  className="absolute inset-y-0 left-0 rounded-full opacity-60"
                  style={{
                    width: '100%',
                    background: 'linear-gradient(90deg, #7CF1C7, #F5E695, #FF9473)',
                  }}
                />
                {meterAnimating && (
                  <div
                    className="absolute -top-2 h-8 w-3 rounded-full bg-white shadow-[0_2px_8px_rgba(0,0,0,0.4)]"
                    style={{ left: `${tickerPosition}%`, transform: 'translateX(-50%)' }}
                  />
                )}
              </div>
              <div className="flex justify-between px-1 text-sm" style={{ color: 'rgba(255,255,255,0.8)' }}>
                <span>Below average</span>
                <span>Average</span>
                <span>Above average</span>
              </div>
            </div>
            <p className="mt-4 text-sm text-white/55">
              Catch tells you how your risk stacks up against other people like you, and why.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
